import { 
  Container, 
  Title, 
  Text, 
  Stack, 
  Image, 
  Grid, 
  Box,
  Paper,
  Center,
  ThemeIcon,
  Button
} from '@mantine/core';
import { IconCheck, IconHeart, IconSalad } from '@tabler/icons-react';
import { AuthButton } from '../components/AuthButton';
import { GradientText } from '../components/UI/GradientText';
import { FadeIn } from '../components/UI/FadeIn';
import { heroFontFamily } from '../theme';
import { useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link } from 'react-router-dom';

export const AboutPage = () => {
  const { user } = useAuth();
  usePageTitle('About | Meals You Love');

  return (
    <Box>
      {/* Hero Section */}
      <Container size="lg" py={{ base: 60, md: 100 }}>
        <Stack gap="xl" align="center" ta="center">
          <Title order={1} c="dark" style={{ fontFamily: heroFontFamily }}>
            Meet the family behind{' '}
            <GradientText>Meals You Love</GradientText>
          </Title>

          <Text size="lg" c="dimmed" maw={700} lh={1.6}>
            A family-built solution to the mealtime mental load,
            created by a software engineer for his wife and now helping busy families and individuals everywhere.
          </Text>
        </Stack>
      </Container>

      {/* Family Photo & Story Section */}
      <Box 
        style={{
          background: `linear-gradient(135deg, var(--mantine-color-gray-0) 0%, rgba(255, 250, 245, 1) 100%)`,
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)'
        }}
      >
        <Container size="lg" py={{ base: 60, md: 100 }}>
          <Grid align="center" gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FadeIn>
              <Center>
                <Image
                  src="/images/family.png"
                  alt="Tyler and Maria's family"
                  radius="xl"
                  maw={400}
                  style={{
                    boxShadow: 'var(--mantine-shadow-xl)'
                  }}
                />
              </Center>
              </FadeIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <FadeIn delay={0.15}>
              <Stack gap="lg">
                <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                  Our story
                </Title>
                <Text size="lg" c="dimmed" lh={1.6}>
                  Hi, I'm Tyler, a software engineer living in Colorado with my lovely wife and daughter. 
                  My wife Maria is an incredible cook, but between running her family's business operations,
                  being an amazing mom, and managing our household, she was drowning in the mental load of
                  meal planning and grocery shopping.
                </Text>
                <Text size="lg" c="dimmed" lh={1.6}>
                  Like so many families, we were stuck in the endless cycle of <em>"what's for dinner?"</em> followed by scrambling for ingredients. When Maria did find recipes online, 
                  they came buried in sites designed for ad revenue, not cooking.
                </Text>
                <Text size="lg" c="dimmed" lh={1.6}>
                  And so I got to work. Maria isn't just my first customer, she's also my product manager.
                  Together, we're building something to make mealtime easier for everyone, whether you're
                  managing a busy household or just trying to eat well as an individual with a packed
                  schedule. From our kitchen chaos to yours, spend less time stressing and more time
                  enjoying the meals you love. 
                </Text>
                
                <Text size="md" c="orange" fw={500} style={{ fontStyle: 'italic', marginTop: '1rem', fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  — Tyler
                </Text>
              </Stack>
              </FadeIn>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Philosophy Section */}
      <Box 
        style={{
          background: `linear-gradient(135deg, var(--mantine-color-gray-0) 0%, rgba(255, 250, 245, 1) 100%)`,
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)'
        }}
      >
        <Container size="lg" py={{ base: 60, md: 100 }}>
          <Stack gap="xl">
            <FadeIn>
            <Center>
              <Stack gap="sm" align="center" maw={700}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  Our philosophy
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  Built for real kitchens, real diets, real life
                </Text>
              </Stack>
            </Center>
            </FadeIn>

            <Grid gutter="lg">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <FadeIn style={{ height: '100%' }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="center" ta="center">
                    <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                      <IconSalad size={30} />
                    </ThemeIcon>
                    <Title order={4} c="dark">Meals, not models</Title>
                    <Text c="dimmed" lh={1.5}>
                      AI is the tool, but the goal is simple: <em>dinner on the table</em>. We build for real kitchens, not theoretical outputs.
                    </Text>
                  </Stack>
                </Paper>
                </FadeIn>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <FadeIn delay={0.1} style={{ height: '100%' }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="center" ta="center">
                    <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                      <IconCheck size={30} />
                    </ThemeIcon>
                    <Title order={4} c="dark">No AI slop</Title>
                    <Text c="dimmed" lh={1.5}>
                      We use AI like a good ingredient — <em>thoughtfully</em>. We meticulously refine our models for quality recipes and smart shopping, paired with features that solve everyday problems.
                    </Text>
                  </Stack>
                </Paper>
                </FadeIn>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <FadeIn delay={0.2} style={{ height: '100%' }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="center" ta="center">
                    <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                      <IconHeart size={30} />
                    </ThemeIcon>
                    <Title order={4} c="dark">Shared table</Title>
                    <Text c="dimmed" lh={1.5}>
                      We know there will be rough edges. We'll keep shipping improvements with your feedback as the secret ingredient.
                    </Text>
                  </Stack>
                </Paper>
                </FadeIn>
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Final Message Section */}
      <Container size="lg" py={{ base: 60, md: 100 }}>
        <FadeIn>
        <Center>
          <Stack gap="xl" align="center" ta="center" maw={800}>
            <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
              Join our growing family
            </Title>
            <Text size="lg" c="dimmed" lh={1.6}>
              Your subscription doesn't just give you a meal planner that fits your life.
              It allows us to continue improving the service, adding new features, and helping
              more busy people reclaim their time and reduce mealtime stress.
            </Text>
            <Text size="lg" c="dimmed" lh={1.6}>
              This is a service thoughtfully designed and built with love, 
              tested in real kitchens, and refined based on real feedback. 
              We can't wait to help your family fall in love with mealtime again.
            </Text>
            
            {user ? (
              <Button 
                size="xl"
                variant="gradient"
                gradient={{ from: 'orange', to: 'red', deg: 45 }}
                component={Link}
                to="/"
              >
                Go to Dashboard
              </Button>
            ) : (
              <AuthButton size="xl">
                Start your free trial
              </AuthButton>
            )}
          </Stack>
        </Center>
        </FadeIn>
      </Container>
    </Box>
  );
};
